/* Roles — which features each kind of person at this salon may use.
 *
 * A role is a name and a set of feature ids, and every member holds exactly
 * one. What somebody can use is what the salon is licensed for AND what their
 * role allows — 15-roles.sql takes that intersection in `tenant_entitlements()`,
 * which is the question every screen in the console already asks before it
 * draws. A withheld feature is not offered at all: not in the module switcher,
 * not as a settings card, not as the lobby page.
 *
 * Unlike a licence, it is also enforced on the rows. A role without
 * `appointments` reads no appointment, client or sale from PostgREST however
 * the request is made, because a restrictive policy on each of those tables
 * asks `private.role_allows()`. See the table list in 15-roles.sql — that list,
 * not this page, is what a feature's data actually is.
 *
 * ── WHAT THIS PAGE WILL NOT DO ──────────────────────────────────────────────
 *
 * Delete a role somebody holds. The foreign key from tenant_members refuses it
 * whatever route asks; the button says why before it is pressed.
 *
 * Delete `owner` or `station`. The first is what manages the salon's accounts,
 * the second is what every route writes when it names no role. Both may be
 * renamed and given different features.
 *
 * Change a role's id. It is what members point at. Rename it instead.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { store } from '../store.js';
import { nav } from '../nav.js';
import { licensing } from '../rules/licensing.js';
import { roles as rules } from '../rules/roles.js';
import { ConfirmButton } from '../components/confirm-button.js';

var Roles = {
  name: 'AdminRoles',
  components: { ConfirmButton: ConfirmButton },
  setup: function () {
    var members = Vue.ref([]);
    var loaded = Vue.ref(false);
    var creating = Vue.ref(false);
    var draft = Vue.reactive({ id: '', name: '', features: [] });

    async function load() {
      var id = store.selectedId.value;
      if (!id) return;
      loaded.value = false;
      store.loadLicense(id);
      await store.loadRoles(id, true);
      var rows = await api.rows('tenant_members', id);
      if (rows) members.value = rows;
      loaded.value = true;
    }

    Vue.watch(store.selectedId, load, { immediate: true });

    var held = Vue.computed(function () { return rules.holders(members.value); });

    // What the salon's licence covers, or null while it is not known — in which
    // case nothing is marked as withheld rather than everything.
    var licensed = Vue.computed(function () {
      if (store.license.loadedFor !== store.selectedId.value) return null;
      return store.entitlement.value.features || [];
    });

    // Every feature this build knows, plus any a role already carries that it
    // does not — the same rule the Plans screen applies.
    var featureRows = Vue.computed(function () {
      var seen = {};
      var out = licensing.FEATURES.map(function (f) {
        seen[f.id] = true;
        return { id: f.id, label: f.label, hint: f.hint, known: true };
      });
      store.roles.rows.forEach(function (r) {
        (r.features || []).forEach(function (id) {
          if (seen[id] || !id) return;
          seen[id] = true;
          out.push({
            id: id, label: id, known: false,
            hint: 'This build of the admin app does not know this feature. It is kept exactly as it is.'
          });
        });
      });
      return out;
    });

    function onPlan(id) { return !licensed.value || licensed.value.indexOf(id) !== -1; }

    var rows = Vue.computed(function () {
      return store.roles.rows.map(function (r) {
        var n = held.value[r.id] || 0;
        return {
          role: r,
          builtIn: rules.isBuiltIn(r.id),
          holders: n,
          effective: rules.effective(r.features, licensed.value),
          refuseDelete: rules.refuseDelete(r, members.value),
          // What a feature toggle's confirmation has to say: who feels it.
          reach: n === 0 ? 'nobody holds this role'
            : n === 1 ? '1 person holds this role'
            : n + ' people hold this role'
        };
      });
    });

    // Why a feature cannot be added to this role (owner-only ones), or null.
    function refusedToggle(role, id) { return rules.refuseToggle(role, id); }

    function has(role, id) { return (role.features || []).indexOf(id) !== -1; }

    async function toggleFeature(role, id) {
      await store.saveRole(role.id, { features: rules.toggle(role.features, id) });
    }

    async function rename(role, value) {
      var name = String(value || '').trim();
      if (name === role.name) return;
      var why = rules.refuseRename(role, name, store.roles.rows);
      if (why) { api.toast(why, 'bad'); return; }
      await store.saveRole(role.id, { name: name });
    }

    async function remove(role) {
      var why = rules.refuseDelete(role, members.value);
      if (why) { api.toast(why, 'bad'); return; }
      if (await store.deleteRole(role.id)) api.toast('Deleted ' + role.name + '.');
    }

    /* The draft as the rule sees it. A new role is never `owner` (refuseCreate
     * says that name is taken), so an owner-only feature is refused here
     * whatever id has been typed so far; the trigger in part 16 would refuse
     * the insert anyway. */
    function draftRole() {
      return { id: draft.id.trim() || rules.slugFor(draft.name), features: draft.features };
    }
    function refusedDraft(id) { return rules.refuseToggle(draftRole(), id); }

    function toggleDraft(id) {
      if (refusedDraft(id)) return;
      draft.features = rules.toggle(draft.features, id);
    }

    async function create() {
      var row = {
        id: draft.id.trim() || rules.slugFor(draft.name),
        name: draft.name.trim(),
        features: licensing.normalise(draft.features),
        sort_order: 100 + store.roles.rows.length * 10
      };
      var why = rules.refuseCreate(row, store.roles.rows);
      if (why) { api.toast(why, 'bad'); return; }
      if (!(await store.createRole(row))) return;
      draft.id = '';
      draft.name = '';
      draft.features = [];
      creating.value = false;
      api.toast('Created. Give it to somebody on the People page.');
    }

    return {
      rows: rows,
      loaded: loaded,
      missing: Vue.computed(function () { return store.roles.missing; }),
      featureRows: featureRows,
      has: has,
      onPlan: onPlan,
      toggleFeature: toggleFeature,
      refusedToggle: refusedToggle,
      rename: rename,
      remove: remove,
      creating: creating,
      draft: draft,
      toggleDraft: toggleDraft,
      refusedDraft: refusedDraft,
      create: create,
      slugFor: rules.slugFor,
      label: function (id) {
        var meta = licensing.featureMeta(id);
        return meta ? meta.label : id;
      },
      goPage: nav.goPage
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>Roles</h2>',
    '    <p class="page__lead">Which features each kind of person at this salon may use. Somebody',
    '      gets a feature only when the salon&rsquo;s',
    '      <button type="button" class="link-btn" @click="goPage(\'licensing\')">licence</button>',
    '      covers it <em>and</em> their role allows it. A feature their role withholds is not',
    '      offered anywhere in their console, and its data &mdash; appointments, clients, sales,',
    '      check-ins &mdash; reads as empty from the database itself.</p>',
    '  </div>',
    '',
    '  <p v-if="missing" class="warn-line">Roles are not set up on this project. Run',
    '    <code>admin/server/schema/15-roles.sql</code> &mdash; until then every member can use',
    '    everything the licence covers.</p>',
    '',
    '  <template v-else>',
    '    <div class="screen__actions">',
    '      <button type="button" class="btn btn--primary" @click="creating = !creating">',
    '        {{ creating ? "Cancel" : "New role" }}</button>',
    '    </div>',
    '',
    '    <form v-if="creating" class="card" @submit.prevent="create">',
    '      <h3>New role</h3>',
    '      <div class="card__row">',
    '        <label class="field field--grow">',
    '          <span class="field__label">Name</span>',
    '          <input v-model="draft.name" class="input" type="text" maxlength="60"',
    '            placeholder="Front desk, Lobby tablet, Manager…" />',
    '        </label>',
    '        <label class="field">',
    '          <span class="field__label">Id</span>',
    '          <input v-model="draft.id" class="input input--key mono" type="text" maxlength="40"',
    '            :placeholder="slugFor(draft.name) || \'front-desk\'" />',
    '        </label>',
    '      </div>',
    '      <div class="chips">',
    '        <button v-for="f in featureRows" :key="f.id" type="button" class="chip"',
    '          :class="{ \'chip--on\': draft.features.includes(f.id) }"',
    '          :disabled="!!refusedDraft(f.id)" :title="refusedDraft(f.id) || null"',
    '          @click="toggleDraft(f.id)">{{ f.label }}</button>',
    '      </div>',
    '      <template v-for="f in featureRows" :key="\'why-\' + f.id">',
    '        <p v-if="refusedDraft(f.id)" class="hint">{{ refusedDraft(f.id) }}</p>',
    '      </template>',
    '      <p class="hint">The id is what each member&rsquo;s row points at, so it is fixed once the',
    '        role exists. The name can change at any time.</p>',
    '      <div class="card__row">',
    '        <button type="submit" class="btn btn--primary">Create</button>',
    '      </div>',
    '    </form>',
    '',
    '    <p v-if="!loaded" class="empty">Loading…</p>',
    '',
    '    <ul v-else class="rows">',
    '      <li v-for="r in rows" :key="r.role.id" class="row row--stack">',
    '        <div class="row__head">',
    '          <span class="row__name">{{ r.role.name }}</span>',
    '          <span class="row__key mono">{{ r.role.id }}</span>',
    '          <span v-if="r.builtIn" class="tag">built in</span>',
    '          <span class="row__end">',
    '            <span class="pill" :class="r.holders ? \'pill--ok\' : \'\'">',
    '              {{ r.holders }} {{ r.holders === 1 ? "person" : "people" }}</span>',
    '          </span>',
    '        </div>',
    '',
    '        <div class="row__body">',
    '          <div class="card__row">',
    '            <label class="field field--grow">',
    '              <span class="field__label">Name</span>',
    '              <input class="input" type="text" maxlength="60" :value="r.role.name"',
    '                @change="rename(r.role, $event.target.value)" />',
    '            </label>',
    '          </div>',
    '',
    '          <p class="hint">Gets today:',
    '            <strong v-if="r.effective.length">{{ r.effective.map(label).join(", ") }}</strong>',
    '            <strong v-else>nothing &mdash; somebody in this role signs in to an empty',
    '              console</strong>.</p>',
    '',
    '          <ul class="rows rows--tight">',
    '            <li v-for="f in featureRows" :key="f.id" class="row row--slim">',
    '              <span class="row__main">',
    '                <span class="row__name">{{ f.label }}',
    '                  <span v-if="!f.known" class="tag tag--bad">unknown to this build</span>',
    '                  <span v-if="has(r.role, f.id) && !onPlan(f.id)" class="tag"',
    '                    title="The salon is not licensed for this, so nobody gets it until it is.">',
    '                    not on their licence</span></span>',
    '                <span class="row__desc">{{ f.hint }}</span>',
    '                <span v-if="refusedToggle(r.role, f.id)" class="hint hint--inline">',
    '                  {{ refusedToggle(r.role, f.id) }}</span>',
    '              </span>',
    '              <span class="row__key mono">{{ f.id }}</span>',
    '              <confirm-button :label="has(r.role, f.id) ? \'Remove\' : \'Add\'" size="sm"',
    '                :kind="has(r.role, f.id) ? \'danger\' : \'quiet\'"',
    '                :disabled="!!refusedToggle(r.role, f.id)"',
    '                :confirm="(has(r.role, f.id) ? \'Take away — \' : \'Allow — \') + r.reach"',
    '                @confirmed="toggleFeature(r.role, f.id)" />',
    '            </li>',
    '          </ul>',
    '',
    '          <div class="card__row">',
    '            <confirm-button label="Delete role" size="sm" :disabled="!!r.refuseDelete"',
    '              confirm="Delete this role" @confirmed="remove(r.role)" />',
    '            <span v-if="r.refuseDelete" class="hint hint--inline">{{ r.refuseDelete }}</span>',
    '          </div>',
    '        </div>',
    '      </li>',
    '    </ul>',
    '',
    '    <div class="card">',
    '      <h3>When a change reaches the salon</h3>',
    '      <p class="hint">The data side is immediate: the next query a console makes is answered',
    '        under the new role. The screens follow when the console next asks what it is',
    '        entitled to &mdash; on sign-in, and every quarter of an hour after. Taking',
    '        <strong>Turn Tracker</strong> away from a role somebody is using right now empties',
    '        their board at once, so do that between shifts.</p>',
    '    </div>',
    '  </template>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'roles',
  name: 'Roles',
  group: 'Salon',
  page: Roles
});

export { Roles };
